import React from 'react';
import { useApp } from './context/AppContext';
import { NavigationRibbon } from './components/NavigationRibbon';
import { UpdateBanner } from './components/UpdateBanner';
import { BottomTabBar } from './components/BottomTabBar';
import { SettingsModal } from './components/SettingsModal';
import { AllDecksModal } from './components/AllDecksModal';
import { IdentityAuthModal } from './components/IdentityAuthModal';
import { AutoDetectBanner } from './components/AutoDetectBanner';
import { BioSyncModal } from './components/BioSyncModal';
import { NotificationSettingsModal } from './components/NotificationSettingsModal';
import { PrimeDeckView } from './views/PrimeDeckView';
import { DiagnosticsBenchView } from './views/DiagnosticsBenchView';
import { DeviceDecksView } from './views/DeviceDecksView';
import { StageRigDeckView } from './views/StageRigDeckView';
import { FissalChatView } from './views/FissalChatView';
import { HomelabCockpitView } from './views/HomelabCockpitView';
import { WebTerminalView } from './views/WebTerminalView';
import { SmartHomeDeckView } from './views/SmartHomeDeckView';
import { RedfurCommunityView } from './views/RedfurCommunityView';
import { FamilyPhotoVaultView } from './views/FamilyPhotoVaultView';
import { FamilyCamerasView } from './views/FamilyCamerasView';
import { FamilyLightsView } from './views/FamilyLightsView';
import { FamilyDeskNoteView } from './views/FamilyDeskNoteView';
import { FamilyDeviceManagerView } from './views/FamilyDeviceManagerView';

import { FamilyLivingRoomView } from './views/FamilyLivingRoomView';

export const AppContent: React.FC = () => {
  const {
    activeLayer,
    activeTab,
    allDecksModalOpen,
    setAllDecksModalOpen,
    bioModalOpen,
    setBioModalOpen,
    notificationModalOpen,
    setNotificationModalOpen
  } = useApp();

  const renderActiveView = () => {
    // 1. DEVOPS & FIELD OPS SUITE (Available to anyone, primarily Echo 🛠️)
    if (activeLayer === 'devops') {
      switch (activeTab) {
        case 'cockpit':
        case 'prime':
          return <PrimeDeckView />;
        case 'server':
          return <HomelabCockpitView />;
        case 'diagnostics':
          return <DiagnosticsBenchView />;
        case 'terminal':
          return <WebTerminalView />;
        case 'fissal':
          return <FissalChatView />;
        case 'devices':
        case 'decks':
          return <DeviceDecksView />;
        case 'cameras':
        case 'smarthome':
          return <SmartHomeDeckView />;
        case 'note':
          return <FamilyDeskNoteView />;
        case 'stagerig':
          return <StageRigDeckView />;
        default:
          return <PrimeDeckView />;
      }
    }

    // 2. FAMILY SUITE (Prioritized At-A-Glance Household Controls 🏡)
    switch (activeTab) {
      case 'cameras':
        return <FamilyCamerasView />;
      case 'fissal':
      case 'concierge':
        return <FissalChatView />;
      case 'devices':
        return <FamilyDeviceManagerView />;
      case 'living':
        return <FamilyLivingRoomView />;
      case 'lights':
        return <FamilyLightsView />;
      case 'photos':
        return <FamilyPhotoVaultView />;
      case 'note':
        return <FamilyDeskNoteView />;
      default:
        return <FamilyCamerasView />;
    }
  };

  return (
    <div className="w-full h-full min-h-[100dvh] flex flex-col overflow-hidden bg-[#070a0f] text-slate-100 relative">
      {/* Top Mobile Ribbon with Sovereign Identity Badge & Bio-Sync */}
      <NavigationRibbon />

      {/* Non-intrusive Hardware Auto-Detect Banner */}
      <AutoDetectBanner />

      {/* Sovereign OTA Update Banner */}
      <UpdateBanner />

      {/* Primary Scrollable Viewport */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {renderActiveView()}
      </main>

      {/* Bottom Tab Bar Filtered by Role */}
      <BottomTabBar />

      {/* Sovereign Identity Auth Modal */}
      <IdentityAuthModal />

      {/* Settings Modal */}
      <SettingsModal />

      {/* Sovereign Notification Deck & Cooldowns */}
      <NotificationSettingsModal isOpen={notificationModalOpen} onClose={() => setNotificationModalOpen(false)} />

      {/* Sovereign Bio-Harmonic Live Resonance Modal */}
      <BioSyncModal isOpen={bioModalOpen} onClose={() => setBioModalOpen(false)} />

      {/* Sovereign All Decks Index Modal */}
      <AllDecksModal isOpen={allDecksModalOpen} onClose={() => setAllDecksModalOpen(false)} />
    </div>
  );
};

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🐾 [Fissal ErrorBoundary] Caught render exception:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full min-h-[100dvh] flex flex-col items-center justify-center bg-[#070a0f] text-slate-100 p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-fissal-gold/40 flex items-center justify-center text-3xl mb-4 shadow-quantum-glow animate-pulse">
            🐾
          </div>
          <h1 className="font-cinzel text-lg font-bold text-fissal-gold mb-2">Fissal Prime Shield Active</h1>
          <p className="text-xs text-slate-300 max-w-sm mb-4 leading-relaxed">
            Whiskers caught a client-side glitch before it could bring down the bridge:
          </p>
          <div className="p-3 rounded-xl bg-black/60 border border-white/10 text-[11px] font-mono text-rose-400 max-w-sm mb-6 text-left overflow-auto max-h-32">
            {this.state.error?.message || 'Unexpected render error'}
          </div>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            className="px-5 py-2.5 rounded-xl bg-fissal-gold text-slate-950 font-bold text-xs shadow-quantum-glow hover:bg-amber-400 active:scale-95 transition-all"
          >
            Reload Bridge
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
